// TeamActions — the team's ONE registry and its overlays (ADR-036 §7 — the
// four kinds; the grammar's EXEMPLAR: mirror it for the next entity's module).
//
//   • `teamActs` is the PURE registry — rows with a kind, a section, a gate and
//     a `show` predicate over the status and the closed-over facts; the shape
//     test (`components/patterns/actionGrammar.test.ts`) walks it.
//   • `useTeamActions` resolves it for a target and returns the ACTS every
//     surface composes from — the peek's top row + door through `ContextPanel`,
//     the page's seat + door through `EntityHeader`, each card's own act through
//     `SectionAct` — and the OVERLAYS: every dialog and drawer, mounted ONCE by
//     the caller beside its surface (never inside a door, never twice).
//
// Visibility is DECLARATIVE in the rows: gate `team:manage` × `has_field_force`
// × the status × the server's word — `can_manage` (team:manage over the team's
// scope) for the team's own acts, `can_assign` (the people key with the team
// level, ADR-083 §3) for its chains; a disabled team takes no edit (a write on it
// is refused — ADR-083 §8); Delete is offered only to an EMPTY team (the server
// still refuses a used one, `REFERENCE_IN_USE`, read back in the dialog by the
// reference-delete words). Behaviour stays in the handlers: a disable is a pause
// said in its own dialog (the chains stay — ADR-081 §5's rule for teams), a
// delete is for good. The acts' WORDS: Add members · Edit team · Manage
// responsibilities · Edit work area · Disable / Reactivate · Delete — one string
// each for the row, the card's link and the drawer's title.

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TeamDetail } from '@ems-app/schemas/teams/responses';
import { resolveActs, type ActionItem, type EntityAct } from '../../components/patterns/entityRegistry.js';
import { ChainsDrawer } from '../../components/patterns/ChainsDrawer.js';
import { referenceDeleteRefusal } from '../../components/patterns/ReferenceDelete.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { icons } from '../../components/ui/icons.js';
import { useCan } from '../../hooks/useCan.js';
import { useEntitlements } from '../../hooks/useEntitlements.js';
import { useDeleteTeam, useSetTeamDisabled } from '../../hooks/useTeamMutations.js';
import { TeamFormDrawer } from './TeamFormDrawer.js';
import { AddMembersDrawer } from './AddMembersDrawer.js';
import { WorkAreaDrawer } from './WorkAreaDrawer.js';

export type TeamStatus = 'active' | 'disabled';

/** What each act does — the hook wires these to its overlays. */
export interface TeamActHandlers {
  addMembers: () => void;
  edit: () => void;
  manageResponsibilities: () => void;
  editWorkArea: () => void;
  disable: () => void;
  reactivate: () => void;
  remove: () => void;
}

/** The closed-over facts the rows read beside the status (the server's words). */
export interface TeamActFacts {
  /** `team:manage` over this team's scope (the detail read's `can_manage`). */
  canManage: boolean;
  /** May name this team's chains (the detail read's `can_assign`). */
  canAssign: boolean;
  /** No member — the delete is offered (the server judges the rest). */
  empty: boolean;
}

/** The team's registry — pure: the rows, their kinds, sections and predicates. */
export function teamActs(h: TeamActHandlers, facts: TeamActFacts): ActionItem<TeamStatus>[] {
  const manages = (ctx: { status: TeamStatus }) => facts.canManage && ctx.status === 'active';
  return [
    // The primary — the act people come for: the page's seat, the peek's main
    // button, and nowhere else (one home per act — the Members card carries no link).
    { id: 'add-members', kind: 'primary', labelKey: 'detail.addMembers', icon: icons.add, run: { handler: h.addMembers }, gate: 'team:manage', entitlement: 'has_field_force', order: 10, show: manages },
    // The edits — one per card; the peek's Edit menu holds the three.
    // A disabled team's own details stay editable (ADR-080 §5) — the record's Edit shows whatever its status.
    { id: 'edit', kind: 'edit', section: 'record', labelKey: 'actions.edit', icon: icons.edit, run: { handler: h.edit }, gate: 'team:manage', entitlement: 'has_field_force', order: 10, show: () => facts.canManage },
    // The chains' namer: the people key with the team level (`namer:team` — the key half; `can_assign` is the server's whole word, the team scope included).
    { id: 'manage-responsibilities', kind: 'edit', section: 'responsibilities', labelKey: 'common:chains.manage', icon: icons.responsibilities, run: { handler: h.manageResponsibilities }, gate: 'namer:team', entitlement: 'has_field_force', order: 20, show: (ctx) => facts.canAssign && ctx.status === 'active' },
    { id: 'edit-work-area', kind: 'edit', section: 'worksIn', labelKey: 'detail.editWorkArea', icon: icons.zone, run: { handler: h.editWorkArea }, gate: 'team:manage', entitlement: 'has_field_force', order: 30, show: manages },
    // The lifecycle — the door; Delete is the danger act, fenced last.
    { id: 'disable', kind: 'lifecycle', labelKey: 'actions.disable', icon: icons.statusPaused, run: { handler: h.disable }, gate: 'team:manage', entitlement: 'has_field_force', order: 10, show: (ctx) => facts.canManage && ctx.status === 'active' },
    { id: 'reactivate', kind: 'lifecycle', labelKey: 'actions.reactivate', icon: icons.restore, run: { handler: h.reactivate }, gate: 'team:manage', entitlement: 'has_field_force', order: 10, show: (ctx) => facts.canManage && ctx.status === 'disabled' },
    { id: 'delete', kind: 'lifecycle', labelKey: 'actions.delete', icon: icons.delete, run: { handler: h.remove }, gate: 'team:manage', entitlement: 'has_field_force', order: 20, tone: 'danger', show: () => facts.canManage && facts.empty },
  ];
}

export interface UseTeamActionsOptions {
  /** After a delete (the page navigates back to the list; the peek closes). */
  onDeleted?: () => void;
}

export interface TeamActions {
  /** The resolved set — what the shells and the cards compose from. */
  acts: EntityAct[];
  /** Every dialog and drawer, mounted ONCE by the caller beside its surface. */
  overlays: ReactNode;
}

export function useTeamActions(projectId: string, team: TeamDetail, { onDeleted }: UseTeamActionsOptions = {}): TeamActions {
  const { t } = useTranslation('teams');
  const { t: tc } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const can = useCan(projectId);
  const entitlements = useEntitlements(projectId);
  const setDisabled = useSetTeamDisabled(projectId);
  const remove = useDeleteTeam(projectId);
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [areaOpen, setAreaOpen] = useState(false);
  const [chainsOpen, setChainsOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const status: TeamStatus = team.is_disabled ? 'disabled' : 'active';
  const writable = team.can_manage && !team.is_disabled;

  const acts = resolveActs(
    teamActs(
      {
        addMembers: () => setAddOpen(true),
        edit: () => setEditOpen(true),
        // The chains drawer at its list — the card's ONE door (its rows are content).
        manageResponsibilities: () => setChainsOpen(true),
        editWorkArea: () => setAreaOpen(true),
        disable: () => setDisableOpen(true),
        reactivate: () => setReactivateOpen(true),
        remove: () => setDeleteOpen(true),
      },
      { canManage: team.can_manage, canAssign: team.can_assign, empty: team.members.length === 0 },
    ),
    { can, entitlements, status },
    (key) => t(key),
  );

  const overlays = (
    <>
      {/* The record's drawer stays mounted (it slides) — keyed by the team so a
          peek→peek switch never keeps the previous team's seed (the stale-edit rule). */}
      {team.can_manage && <TeamFormDrawer key={team.id} projectId={projectId} team={team} open={editOpen} onClose={() => setEditOpen(false)} />}
      {writable && <AddMembersDrawer projectId={projectId} team={team} open={addOpen} onClose={() => setAddOpen(false)} />}
      {writable && <WorkAreaDrawer projectId={projectId} team={team} open={areaOpen} onClose={() => setAreaOpen(false)} />}
      {team.can_assign && (
        <ChainsDrawer projectId={projectId} scope={{ level: 'team', id: team.id, name: team.name, closed: team.is_disabled }} open={chainsOpen} onClose={() => setChainsOpen(false)} />
      )}
      <ConfirmDialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        title={t('actions.disableTitle', { name: team.name })}
        description={t('actions.disableBody')}
        confirmLabel={t('actions.disable')}
        tone="default"
        onConfirm={() => setDisabled.mutateAsync({ teamId: team.id, disabled: true }).then(() => undefined)}
      />
      <ConfirmDialog
        open={reactivateOpen}
        onClose={() => setReactivateOpen(false)}
        title={t('actions.reactivateTitle', { name: team.name })}
        description={t('actions.reactivateBody')}
        confirmLabel={t('actions.reactivate')}
        tone="default"
        onConfirm={() => setDisabled.mutateAsync({ teamId: team.id, disabled: false }).then(() => undefined)}
      />
      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title={t('actions.deleteTitle', { name: team.name })}
        description={t('actions.deleteBody')}
        confirmLabel={t('actions.delete')}
        onConfirm={() =>
          remove.mutateAsync(team.id).then(
            () => onDeleted?.(),
            (e: unknown) => {
              // A used team refuses (REFERENCE_IN_USE) — the reference-delete words name what still uses it.
              throw new Error(referenceDeleteRefusal(e, tc, tErrors));
            },
          )
        }
      />
    </>
  );

  return { acts, overlays };
}
